import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API from "../../../utils/apiCalling";
import { config } from "../../../utils/apiUrl";

const api = new API();

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
    );
};

const getResponseBody = (
    response
) => {
    return (
        response?.data ??
        response ??
        {}
    );
};

const getResponseData = (
    response
) => {
    const body =
        getResponseBody(
            response
        );

    return (
        body?.data ??
        body
    );
};

const getPagination = (
    response
) => {
    const body =
        getResponseBody(
            response
        );

    return (
        body?.pagination || {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
        }
    );
};

const getSummary = (
    response
) => {
    const body =
        getResponseBody(
            response
        );

    return (
        body?.summary || {}
    );
};

const buildQueryString = (
    params = {}
) => {
    const query =
        new URLSearchParams();

    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {
            if (
                value === undefined ||
                value === null ||
                value === ""
            ) {
                return;
            }

            query.append(
                key,
                value
            );
        }
    );

    const queryString =
        query.toString();

    return queryString
        ? `?${queryString}`
        : "";
};

export const cancelPurchaseBill =
    createAsyncThunk(
        "purchaseBill/cancelPurchaseBill",

        async (
            {
                purchaseBillId,
                cancellationReason = "",
            },
            {
                rejectWithValue,
            }
        ) => {
            try {
                const response =
                    await api.patch(
                        `${config.cancelPurchaseBill}/${purchaseBillId}/cancel`,
                        {
                            cancellationReason,
                        }
                    );

                return (
                    response?.data?.data ||
                    response?.data ||
                    response
                );
            } catch (
            error
            ) {
                return rejectWithValue(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unable to cancel purchase bill."
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH PURCHASE BILL LIST
|--------------------------------------------------------------------------
*/

export const fetchPurchaseBillList =
    createAsyncThunk(
        "purchaseBill/fetchList",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {
            try {
                const queryString =
                    buildQueryString({
                        page:
                            params.page,

                        limit:
                            params.limit,

                        search:
                            params.search,

                        supplierId:
                            params.supplierId,

                        status:
                            params.status,

                        paymentStatus:
                            params.paymentStatus,

                        fromDate:
                            params.fromDate,

                        toDate:
                            params.toDate,
                    });

                const response =
                    await api.get(
                        `${config.getPurchaseBillList()}${queryString}`
                    );

                return {
                    records:
                        getResponseData(
                            response
                        ) || [],

                    pagination:
                        getPagination(
                            response
                        ),

                    summary:
                        getSummary(
                            response
                        ),
                };
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load purchase bills."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| FETCH PURCHASE BILL BY ID
|--------------------------------------------------------------------------
*/

export const fetchPurchaseBillById =
    createAsyncThunk(
        "purchaseBill/fetchById",

        async (
            purchaseBillId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !purchaseBillId
                ) {
                    throw new Error(
                        "Purchase bill ID is required."
                    );
                }

                const response =
                    await api.get(
                        config.getPurchaseBillById(
                            purchaseBillId
                        )
                    );

                return (
                    getResponseData(
                        response
                    ) || null
                );
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to load purchase bill."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| CREATE PURCHASE BILL
|--------------------------------------------------------------------------
*/

export const createPurchaseBill =
    createAsyncThunk(
        "purchaseBill/create",

        async (
            payload,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !payload
                        ?.supplierId
                ) {
                    throw new Error(
                        "Supplier is required."
                    );
                }

                if (
                    !Array.isArray(
                        payload
                            ?.items
                    ) ||
                    payload
                        .items
                        .length ===
                    0
                ) {
                    throw new Error(
                        "At least one purchase item is required."
                    );
                }

                const response =
                    await api.post(
                        config.createPurchaseBill(),
                        payload
                    );

                return (
                    getResponseData(
                        response
                    ) || null
                );
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to create purchase bill."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| UPDATE PURCHASE BILL
|--------------------------------------------------------------------------
*/

export const updatePurchaseBill =
    createAsyncThunk(
        "purchaseBill/update",

        async (
            {
                purchaseBillId,
                payload,
            },
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !purchaseBillId
                ) {
                    throw new Error(
                        "Purchase bill ID is required."
                    );
                }

                const response =
                    await api.put(
                        config.updatePurchaseBill(
                            purchaseBillId
                        ),
                        payload
                    );

                return (
                    getResponseData(
                        response
                    ) || null
                );
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to update purchase bill."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| CONFIRM PURCHASE BILL
|--------------------------------------------------------------------------
*/

export const confirmPurchaseBill =
    createAsyncThunk(
        "purchaseBill/confirm",

        async (
            purchaseBillId,
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !purchaseBillId
                ) {
                    throw new Error(
                        "Purchase bill ID is required."
                    );
                }

                const response =
                    await api.patch(
                        config.confirmPurchaseBill(
                            purchaseBillId
                        ),
                        {}
                    );

                return (
                    getResponseData(
                        response
                    ) || null
                );
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to confirm purchase bill."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| REFRESH PURCHASE BILL BALANCE
|--------------------------------------------------------------------------
*/

export const refreshPurchaseBillBalance =
    createAsyncThunk(
        "purchaseBill/refreshBalance",

        async (
            {
                purchaseBillId,
                paidAmount,
                debitNoteAmount,
            },
            {
                rejectWithValue,
            }
        ) => {
            try {
                if (
                    !purchaseBillId
                ) {
                    throw new Error(
                        "Purchase bill ID is required."
                    );
                }

                const response =
                    await api.patch(
                        config.refreshPurchaseBillBalance(
                            purchaseBillId
                        ),
                        {
                            paidAmount,
                            debitNoteAmount,
                        }
                    );

                return (
                    getResponseData(
                        response
                    ) || null
                );
            } catch (
            error
            ) {
                return rejectWithValue(
                    getErrorMessage(
                        error,
                        "Unable to refresh purchase bill balance."
                    )
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
    /*
    |--------------------------------------------------------------------------
    | LIST
    |--------------------------------------------------------------------------
    */

    records: [],

    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },

    summary: {
        totalBills: 0,
        totalPurchaseAmount: 0,
        totalPaidAmount: 0,
        totalOutstanding: 0,
        totalDebitNoteAmount: 0,
    },

    /*
    |--------------------------------------------------------------------------
    | DETAILS
    |--------------------------------------------------------------------------
    */

    selectedPurchaseBill:
        null,

    /*
    |--------------------------------------------------------------------------
    | CREATE / UPDATE RESULT
    |--------------------------------------------------------------------------
    */

    createdPurchaseBill:
        null,

    updatedPurchaseBill:
        null,

    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    loading: {
        list: false,
        details: false,
        create: false,
        update: false,
        confirm: false,
        refreshBalance: false,
    },

    /*
    |--------------------------------------------------------------------------
    | ERROR
    |--------------------------------------------------------------------------
    */

    error: {
        list: null,
        details: null,
        create: null,
        update: null,
        confirm: null,
        refreshBalance: null,
    },
};

/*
|--------------------------------------------------------------------------
| HELPER
|--------------------------------------------------------------------------
*/

const updateRecordById = (
    records,
    updatedRecord
) => {
    if (
        !Array.isArray(
            records
        ) ||
        !updatedRecord
            ?._id
    ) {
        return records;
    }

    return records.map(
        (
            record
        ) =>
            String(
                record
                    ?._id ||
                ""
            ) ===
                String(
                    updatedRecord
                        ._id
                )
                ? updatedRecord
                : record
    );
};

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const purchaseBillSlice =
    createSlice({
        name:
            "purchaseBill",

        initialState,

        reducers: {
            clearSelectedPurchaseBill: (
                state
            ) => {
                state.selectedPurchaseBill =
                    null;

                state.error.details =
                    null;
            },

            clearCreatedPurchaseBill: (
                state
            ) => {
                state.createdPurchaseBill =
                    null;

                state.error.create =
                    null;
            },

            clearUpdatedPurchaseBill: (
                state
            ) => {
                state.updatedPurchaseBill =
                    null;

                state.error.update =
                    null;
            },

            clearPurchaseBillErrors: (
                state
            ) => {
                state.error = {
                    list: null,
                    details: null,
                    create: null,
                    update: null,
                    confirm: null,
                    refreshBalance: null,
                };
            },

            resetPurchaseBillState: (
                state
            ) => {
                Object.assign(
                    state,
                    initialState
                );
            },
        },

        extraReducers: (
            builder
        ) => {
            /*
            |--------------------------------------------------------------------------
            | LIST
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchaseBillList.pending,
                    (
                        state
                    ) => {
                        state.loading.list =
                            true;

                        state.error.list =
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseBillList.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.records =
                            Array.isArray(
                                action
                                    .payload
                                    ?.records
                            )
                                ? action
                                    .payload
                                    .records
                                : [];

                        state.pagination =
                            action
                                .payload
                                ?.pagination ||
                            state.pagination;

                        state.summary = {
                            ...state.summary,

                            ...(action
                                .payload
                                ?.summary ||
                                {}),
                        };
                    }
                )

                .addCase(
                    fetchPurchaseBillList.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.list =
                            false;

                        state.error.list =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load purchase bills.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | DETAILS
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    fetchPurchaseBillById.pending,
                    (
                        state
                    ) => {
                        state.loading.details =
                            true;

                        state.error.details =
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseBillById.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.selectedPurchaseBill =
                            action.payload ||
                            null;
                    }
                )

                .addCase(
                    fetchPurchaseBillById.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.details =
                            false;

                        state.error.details =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to load purchase bill.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CREATE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    createPurchaseBill.pending,
                    (
                        state
                    ) => {
                        state.loading.create =
                            true;

                        state.error.create =
                            null;

                        state.createdPurchaseBill =
                            null;
                    }
                )

                .addCase(
                    createPurchaseBill.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        state.createdPurchaseBill =
                            action.payload ||
                            null;

                        state.selectedPurchaseBill =
                            action.payload ||
                            null;

                        if (
                            action.payload
                                ?._id
                        ) {
                            state.records = [
                                action.payload,

                                ...state.records.filter(
                                    (
                                        record
                                    ) =>
                                        String(
                                            record
                                                ?._id ||
                                            ""
                                        ) !==
                                        String(
                                            action
                                                .payload
                                                ._id
                                        )
                                ),
                            ];

                            state.pagination.total =
                                Number(
                                    state
                                        .pagination
                                        .total ||
                                    0
                                ) + 1;
                        }
                    }
                )

                .addCase(
                    createPurchaseBill.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.create =
                            false;

                        state.error.create =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to create purchase bill.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | UPDATE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    updatePurchaseBill.pending,
                    (
                        state
                    ) => {
                        state.loading.update =
                            true;

                        state.error.update =
                            null;

                        state.updatedPurchaseBill =
                            null;
                    }
                )

                .addCase(
                    updatePurchaseBill.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.update =
                            false;

                        state.updatedPurchaseBill =
                            action.payload ||
                            null;

                        state.selectedPurchaseBill =
                            action.payload ||
                            state.selectedPurchaseBill;

                        state.records =
                            updateRecordById(
                                state.records,
                                action.payload
                            );
                    }
                )

                .addCase(
                    updatePurchaseBill.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.update =
                            false;

                        state.error.update =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to update purchase bill.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | CONFIRM
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    confirmPurchaseBill.pending,
                    (
                        state
                    ) => {
                        state.loading.confirm =
                            true;

                        state.error.confirm =
                            null;
                    }
                )

                .addCase(
                    confirmPurchaseBill.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.confirm =
                            false;

                        state.selectedPurchaseBill =
                            action.payload ||
                            state.selectedPurchaseBill;

                        state.records =
                            updateRecordById(
                                state.records,
                                action.payload
                            );
                    }
                )

                .addCase(
                    confirmPurchaseBill.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.confirm =
                            false;

                        state.error.confirm =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to confirm purchase bill.";
                    }
                );

            /*
            |--------------------------------------------------------------------------
            | REFRESH BALANCE
            |--------------------------------------------------------------------------
            */

            builder
                .addCase(
                    refreshPurchaseBillBalance.pending,
                    (
                        state
                    ) => {
                        state.loading.refreshBalance =
                            true;

                        state.error.refreshBalance =
                            null;
                    }
                )

                .addCase(
                    refreshPurchaseBillBalance.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.refreshBalance =
                            false;

                        state.selectedPurchaseBill =
                            action.payload ||
                            state.selectedPurchaseBill;

                        state.records =
                            updateRecordById(
                                state.records,
                                action.payload
                            );
                    }
                )

                .addCase(
                    refreshPurchaseBillBalance.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.refreshBalance =
                            false;

                        state.error.refreshBalance =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to refresh purchase bill balance.";
                    }
                )
            builder
                .addCase(
                    cancelPurchaseBill.pending,
                    (
                        state
                    ) => {
                        state.loading.cancel =
                            true;

                        state.error.cancel =
                            null;
                    }
                )

                .addCase(
                    cancelPurchaseBill.fulfilled,
                    (
                        state,
                        action
                    ) => {
                        state.loading.cancel =
                            false;

                        state.selectedPurchaseBill =
                            action.payload ||
                            state.selectedPurchaseBill;

                        if (
                            action.payload
                                ?._id
                        ) {
                            state.records =
                                state.records.map(
                                    (
                                        bill
                                    ) =>
                                        String(
                                            bill?._id ||
                                            ""
                                        ) ===
                                            String(
                                                action
                                                    .payload
                                                    ._id
                                            )
                                            ? action.payload
                                            : bill
                                );
                        }
                    }
                )

                .addCase(
                    cancelPurchaseBill.rejected,
                    (
                        state,
                        action
                    ) => {
                        state.loading.cancel =
                            false;

                        state.error.cancel =
                            action.payload ||
                            action.error
                                ?.message ||
                            "Unable to cancel purchase bill.";
                    }
                );
        },
    });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    clearSelectedPurchaseBill,
    clearCreatedPurchaseBill,
    clearUpdatedPurchaseBill,
    clearPurchaseBillErrors,
    resetPurchaseBillState,
} =
    purchaseBillSlice.actions;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
|
| Root reducer key:
|
| purchaseBill
|--------------------------------------------------------------------------
*/

export const purchaseBillStateSelector = (
    state
) =>
    state?.purchaseBill ||
    initialState;

export const purchaseBillListSelector = (
    state
) =>
    state?.purchaseBill
        ?.records || [];

export const purchaseBillPaginationSelector = (
    state
) =>
    state?.purchaseBill
        ?.pagination ||
    initialState.pagination;

export const purchaseBillSummarySelector = (
    state
) =>
    state?.purchaseBill
        ?.summary ||
    initialState.summary;

export const selectedPurchaseBillSelector = (
    state
) =>
    state?.purchaseBill
        ?.selectedPurchaseBill ||
    null;

export const createdPurchaseBillSelector = (
    state
) =>
    state?.purchaseBill
        ?.createdPurchaseBill ||
    null;

export const updatedPurchaseBillSelector = (
    state
) =>
    state?.purchaseBill
        ?.updatedPurchaseBill ||
    null;

export const purchaseBillLoadingSelector = (
    state
) =>
    state?.purchaseBill
        ?.loading ||
    initialState.loading;

export const purchaseBillErrorSelector = (
    state
) =>
    state?.purchaseBill
        ?.error ||
    initialState.error;

/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default purchaseBillSlice.reducer;